import Service, { service } from '@ember/service'
import type Store from '@ember-data/store'
import { task } from 'ember-concurrency'
import type Student from 'teacher-ui-client/models/student'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import type SessionService from 'teacher-ui-client/services/session'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import type Teacher from 'teacher-ui-client/models/teacher'

export default class BookLibraryCollectionService extends Service {
  @service store!: Store

  @service session!: SessionService

  private buildCollectionRequestOptions(includeBooks?: boolean) {
    // Ensure that, when including books, a reload is done (meaning, it doesn't return immediately after a peek)
    // Otherwise, it may return the "peeked" collection record immediately, which may not have included books.
    return includeBooks ? { include: 'books,books.book-metadata', reload: true } : {}
  }

  findCollection = task(async (id: string, { includeBooks } = { includeBooks: true }) => {
    const options = this.buildCollectionRequestOptions(includeBooks)
    return this.store.findRecord<BookLibraryCollection>(
      'book-library/collection',
      id,
      // @ts-expect-error - include types not correct
      options,
    )
  })

  loadAllMyCollections = task({ drop: true }, async ({ includeBooks } = { includeBooks: true }) => {
    const options = this.buildCollectionRequestOptions(includeBooks)
    // Query for the books. note, this is not a live array.
    return this.store.query<BookLibraryCollection>(
      'book-library/collection',
      // @ts-expect-error - include types not correct
      { ...options, filter: { teacher_ids: [this.session.currentTeacher.id] } },
    )
  })

  /**
   * A live array of the current teachers' collections. Make sure you've loaded them first.
   */
  get allMyCollections() {
    return (
      this.store
        .peekAll<BookLibraryCollection>('book-library/collection')
        // @ts-expect-error - something wrong with relationship reference types
        .filter((c) => c.belongsTo<Teacher, 'teacher'>('teacher').id() === this.session.currentTeacher.id)
    )
  }

  destroyCollection = task(async (collection) => {
    try {
      await collection.destroyRecord()
      return { ok: true }
    } catch (error) {
      return { ok: false, errors: [error] }
    }
  })

  addBooks = task(async (collection: BookLibraryCollection, books: BookLibraryBook[]) => {
    try {
      collection.books = [...(collection.books ?? []), ...books]
      await collection.save()
      return { ok: true, result: collection }
    } catch (error) {
      return { ok: false, errors: [error] }
    }
  })

  removeBooks = task(async (collection: BookLibraryCollection, books: BookLibraryBook[]) => {
    try {
      const booksIdsToRemove = books.map((book) => book.id)
      collection.books = collection.books?.filter((book) => !booksIdsToRemove.includes(book.id)) ?? []
      await collection.save()
      return { ok: true, result: collection }
    } catch (error) {
      return { ok: false, errors: [error] }
    }
  })

  moveBooks = task(
    async (fromCollection: BookLibraryCollection, toCollection: BookLibraryCollection, books: BookLibraryBook[]) => {
      try {
        await this.addBooks.perform(toCollection, books)
        await this.removeBooks.perform(fromCollection, books)
        return { ok: true }
      } catch (error) {
        return { ok: false, errors: [error] }
      }
    },
  )

  shareWithStudents = task(
    async (collection: BookLibraryCollection, students: Student[], startsAt: Date, expiresAt: Date) => {
      return this.store
        .createRecord<BookLibraryShare>('book-library/share', { collection, startsAt, expiresAt, students })
        .save()
    },
  )
}

declare module '@ember/service' {
  interface Registry {
    'book-library/collection': BookLibraryCollectionService
  }
}
